import { inject, Injectable } from '@angular/core';

import { RuntimeConfigService } from './runtime-config';

/** A single matched-dealer application context passed to the embed. */
export interface InventoryApplication {
  dealerId: string;
  leadId?: string;
  submissionId?: string;
  /**
   * Open-account indicator (sourced from our open-loan flag). The embed
   * consumes this as `openAccount`, matching the team's direct-URL params.
   */
  openAccount?: boolean;
}

export interface InventoryModuleEmbedOptions {
  /** CSS selector for the element the embed renders into, e.g. `#cac-inventory-module-embed`. */
  target: string;
  applications: InventoryApplication[];
  consumerZip: string;
  make?: string[];
  bodyStyle?: string[];
  pageSize?: number;
  onError?: (err: { code: string; message: string }) => void;
  /** Invoked when the embed script tag itself fails to load. */
  onScriptError?: () => void;
  /**
   * Fired once the iframe content has finished loading; `meta.vehicleCount`
   * reports how many vehicles the embed rendered. Threaded into the render
   * options so it persists across `setOptions()` re-renders (late-`leadId`
   * hydration re-renders the embed → `onLoad` fires again).
   */
  onLoad?: (meta: { vehicleCount: number }) => void;
}

/** Public surface of the DVM `window.InventoryModule` global the embed script installs. */
interface InventoryModuleApi {
  render(target: string, options: Record<string, unknown>): void;
  setOptions(options: Record<string, unknown>): void;
}

declare global {
  interface Window {
    InventoryModule?: InventoryModuleApi;
  }
}

/**
 * Loads and drives the DVM inventory-module embed on the prequal confirmation
 * page.
 *
 * The embed's `onLoad`/`onError` callbacks have no `data-*` equivalent, so we
 * use the owning team's recommended programmatic pattern instead of the
 * `data-inventory-module="inline"` auto-scan: inject the script once, then call
 * `InventoryModule.render(target, options)` with the `applications` array and
 * callbacks. Late-arriving per-dealer lead IDs are injected after render via
 * the static `InventoryModule.setOptions({ applications })`.
 *
 * The embed script URL (`DVM_INVENTORY_MODULE_URI`) is read from
 * `RuntimeConfigService.prequalConfig`, hydrated at runtime from
 * `window.prequalConfig` (written by `start-app.sh` in deployed envs and by
 * `scripts/generate-config.js` for local `npm start`).
 *
 * The script is injected at most once per page load; subsequent calls re-render
 * against the already-loaded global.
 */
@Injectable({
  providedIn: 'root',
})
export class InventoryModuleScriptLoaderService {
  private readonly prequalConfig = inject(RuntimeConfigService).prequalConfig;
  private static readonly SCRIPT_ID = 'dvm-inventory-module-embed';

  /**
   * The options last passed to `render` (minus `applications`). `setOptions`
   * re-renders the embed, so the update must carry the same `consumerZip` /
   * `make` / `bodyStyle` / `pageSize` / `onError` context — otherwise the
   * reload loses (e.g.) the consumer ZIP and the embed's inventory fetch fails.
   */
  private lastRenderOptions: Record<string, unknown> = {};

  /**
   * Ensures the embed script is loaded, then renders the inventory module into
   * `opts.target` with the supplied `applications` and callbacks. No-op when
   * `DVM_INVENTORY_MODULE_URI` is missing from runtime config (logs a warning).
   */
  loadInventoryModule(opts: InventoryModuleEmbedOptions): void {
    const scriptSrc = this.prequalConfig?.DVM_INVENTORY_MODULE_URI;
    if (!scriptSrc) {
      console.warn(
        'InventoryModuleScriptLoaderService: DVM_INVENTORY_MODULE_URI is not configured; skipping inventory module load.'
      );
      return;
    }

    // Script already evaluated — render straight away.
    if (window.InventoryModule) {
      this.renderModule(opts);
      return;
    }

    let script = document.getElementById(
      InventoryModuleScriptLoaderService.SCRIPT_ID
    ) as HTMLScriptElement | null;

    if (!script) {
      script = document.createElement('script');
      script.id = InventoryModuleScriptLoaderService.SCRIPT_ID;
      script.src = scriptSrc;
      script.async = true;
      document.head.appendChild(script);
    }

    script.addEventListener('load', () => this.renderModule(opts), {
      once: true,
    });
    script.addEventListener('error', () => opts.onScriptError?.(), {
      once: true,
    });
  }

  /**
   * Dynamically updates the rendered embed's options (e.g. injecting per-dealer
   * `leadId`s that arrived after initial render) via the static
   * `InventoryModule.setOptions`. No-op when the embed is not yet available.
   *
   * `setOptions` re-renders the embed, so the new `applications` are merged on
   * top of the original render options (`consumerZip`, `make`, `bodyStyle`,
   * etc.); sending `applications` alone would drop that context and break the
   * embed's inventory fetch.
   */
  updateApplications(applications: InventoryApplication[]): void {
    window.InventoryModule?.setOptions({
      ...this.lastRenderOptions,
      applications,
    });
  }

  private renderModule(opts: InventoryModuleEmbedOptions): void {
    const options = this.buildRenderOptions(opts);
    this.lastRenderOptions = options;
    window.InventoryModule?.render(opts.target, options);
  }

  private buildRenderOptions(
    opts: InventoryModuleEmbedOptions
  ): Record<string, unknown> {
    const options: Record<string, unknown> = {
      applications: opts.applications,
      consumerZip: opts.consumerZip,
    };

    if (opts.make?.length) {
      options['make'] = opts.make;
    }
    if (opts.bodyStyle?.length) {
      options['bodyStyle'] = opts.bodyStyle;
    }
    if (opts.pageSize) {
      options['pageSize'] = opts.pageSize;
    }
    if (opts.onError) {
      options['onError'] = opts.onError;
    }
    if (opts.onLoad) {
      options['onLoad'] = opts.onLoad;
    }

    return options;
  }
}
